import { useState, useEffect, useRef, useCallback } from 'react';
import { OnboardingState } from '../OnboardingWizard';
import { getGDAPRelationshipStatus } from '../../../services/api';
import {
  Users,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertTriangle,
  Copy,
} from 'lucide-react';

interface Props {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  onNext: () => void;
  setError: (error: string | null) => void;
}

export default function CustomerApprovalStep({ state, updateState, onNext, setError }: Props) {
  const [polling, setPolling] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [approved, setApproved] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(state.gdapStatus || 'approvalPending');
  const [copied, setCopied] = useState(false);
  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const checkStatus = useCallback(async () => {
    if (!state.relationshipId) return;

    setCheckingStatus(true);
    try {
      const result = await getGDAPRelationshipStatus(state.relationshipId);
      setCurrentStatus(result.status);

      if (result.status === 'approved' || result.status === 'active') {
        setApproved(true);
        setPolling(false);
        updateState({
          gdapStatus: result.status,
          customerTenantId: result.customer?.tenantId || null,
          customerDisplayName: result.customer?.displayName || null,
        });

        // Stop polling
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
          pollInterval.current = null;
        }
      } else if (result.status === 'terminated' || result.status === 'terminationRequested') {
        setError('The customer declined or terminated the GDAP relationship');
        setPolling(false);
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
          pollInterval.current = null;
        }
      }
    } catch (err) {
      console.error('Error checking status:', err);
    } finally {
      setCheckingStatus(false);
    }
  }, [state.relationshipId, updateState, setError]);

  const startPolling = useCallback(() => {
    if (pollInterval.current) return;

    setPolling(true);
    pollInterval.current = setInterval(checkStatus, 5000); // Check every 5 seconds
  }, [checkStatus]);

  const stopPolling = useCallback(() => {
    setPolling(false);
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
  }, []);

  useEffect(() => {
    // Check status on mount
    checkStatus();

    return () => {
      // Cleanup on unmount
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [checkStatus]);

  const copyToClipboard = async () => {
    if (!state.approvalUrl) return;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(state.approvalUrl);
      } else {
        // Fallback: select the input text and use execCommand
        if (urlInputRef.current) {
          urlInputRef.current.select();
          urlInputRef.current.setSelectionRange(0, 99999);
          document.execCommand('copy');
        }
      }
      setCopied(true);
      // Start polling since user is about to paste in InPrivate
      startPolling();
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Last resort fallback: select the input so user can Ctrl+C
      if (urlInputRef.current) {
        urlInputRef.current.select();
        urlInputRef.current.setSelectionRange(0, 99999);
        try {
          document.execCommand('copy');
          setCopied(true);
          startPolling();
          setTimeout(() => setCopied(false), 3000);
        } catch {
          // If even execCommand fails, at least the text is selected
          setError('Could not copy automatically. The URL is selected — press Ctrl+C to copy.');
        }
      }
    }
  };

  const getStatusDisplay = () => {
    switch (currentStatus) {
      case 'approved':
      case 'active':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-700',
          text: 'Approved',
          description: 'Customer has approved the GDAP relationship',
        };
      case 'approvalPending':
      case 'created':
        return {
          icon: Clock,
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-700',
          text: 'Pending Approval',
          description: 'Waiting for customer to approve',
        };
      case 'terminated':
      case 'terminationRequested':
        return {
          icon: AlertTriangle,
          color: 'text-red-500',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-700',
          text: 'Declined',
          description: 'Customer declined the request',
        };
      default:
        return {
          icon: Clock,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          text: currentStatus,
          description: 'Checking status...',
        };
    }
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.icon;

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Users className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Customer Approval
            </h2>
            <p className="text-sm text-muted-foreground">
              The customer must approve the GDAP relationship
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Status Card */}
        <div className={`rounded-lg p-4 border ${statusDisplay.bgColor} ${statusDisplay.borderColor}`}>
          <div className="flex items-center">
            <StatusIcon className={`h-5 w-5 ${statusDisplay.color} mr-2`} />
            <span className="font-medium text-foreground">
              Status: {statusDisplay.text}
            </span>
            {(polling || checkingStatus) && (
              <RefreshCw className="h-4 w-4 ml-2 animate-spin text-muted-foreground" />
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {statusDisplay.description}
          </p>
        </div>

        {!approved && (
          <>
            {/* Instructions */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <h3 className="font-medium text-amber-900 dark:text-amber-200 mb-3">
                Approve as {state.companyName || 'the customer'}'s Global Admin
              </h3>
              <ol className="space-y-2 text-sm text-amber-800 dark:text-amber-300">
                <li className="flex items-start">
                  <span className="font-medium mr-2">1.</span>
                  Copy the approval URL below
                </li>
                <li className="flex items-start">
                  <span className="font-medium mr-2">2.</span>
                  Open an <strong>InPrivate</strong> (Edge) or <strong>Incognito</strong> (Chrome) window
                </li>
                <li className="flex items-start">
                  <span className="font-medium mr-2">3.</span>
                  Paste the URL and sign in as <strong>{state.companyName || 'the customer'}'s</strong> Global Admin
                </li>
                <li className="flex items-start">
                  <span className="font-medium mr-2">4.</span>
                  Review the admin roles and click <strong>Approve</strong>
                </li>
              </ol>
            </div>

            {/* Approval URL + Copy Button */}
            <div className="bg-muted rounded-lg p-4">
              <label className="text-sm font-medium text-muted-foreground block mb-2">
                Approval URL
              </label>
              <div className="flex items-center space-x-2">
                <input
                  ref={urlInputRef}
                  type="text"
                  readOnly
                  value={state.approvalUrl || ''}
                  className="input flex-1 text-sm font-mono"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-2 rounded-md font-medium text-sm transition-all flex items-center ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  {copied ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy URL
                    </>
                  )}
                </button>
              </div>
              {copied && (
                <p className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
                  URL copied — paste it in an InPrivate / Incognito window
                </p>
              )}
            </div>

            {/* Check Status Button */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={checkStatus}
                disabled={checkingStatus}
                className="btn-secondary"
              >
                {checkingStatus ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Check Status
                  </>
                )}
              </button>
            </div>

            {polling && (
              <p className="text-sm text-muted-foreground text-center">
                Automatically checking for approval every 5 seconds...{' '}
                <button
                  onClick={stopPolling}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Stop
                </button>
              </p>
            )}
          </>
        )}

        {approved && (
          <>
            {/* Tenant Info */}
            {state.customerTenantId && (
              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                  Customer Tenant Information
                </h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Display Name</dt>
                    <dd className="font-medium text-foreground">
                      {state.customerDisplayName || 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Tenant ID</dt>
                    <dd className="font-mono text-foreground">
                      {state.customerTenantId}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            <div className="pt-4 border-t border-border">
              <button onClick={onNext} className="btn-primary w-full sm:w-auto">
                Continue to Finalize Setup &rarr;
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
